import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { Icon, IconButton, TreeItemEdit } from '@picsio/ui';
import { Folder, NewCollection, Ok } from '@picsio/icons';
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import { MAX_GROUP_DEPTH } from '@picsio/db/src/collections/savedSearches/constants';
import {
  any, arrayOf, bool, func, instanceOf, shape, string,
} from 'prop-types';

import l10n from '../../shared/strings';
import Tooltip from '../Tooltip';
import { collectSiblingSegmentNames } from '../../store/helpers/savedSearches/pathConflict';

import s from './styles.module.scss';

/**
 * Pseudo-path for the root scope row — kept out of the real path domain
 * (which uses NEW_PATH_DELIMITER-prefixed paths) so callers can distinguish
 * "user clicked the scope row" from any group whose path happens to be ''.
 */
export const ROOT_KEY = '__root__';

// Match DropdownTree's `itemPaddingLeft` so visual rhythm is identical to the
// "Add to collection" tree (which we adopt the styling of).
const ITEM_PADDING_LEFT = 15;

const FolderIcon = () => (
  <Icon size="inherit" color="inherit"><Folder /></Icon>
);

/** Checkmark badge shown on the selected row — mirrors DropdownTree. */
const SelectedCheck = () => (
  <div className="iconChecked"><Icon size="sm"><Ok /></Icon></div>
);

/**
 * Recursively project a saved-searches tree to "groups only", excluding
 * readonly + dynamic groups (the server forbids saving / moving searches
 * into them, so they're never valid targets in either picker).
 *
 * `hasChild` is recomputed against the filtered children so a parent whose
 * only descendants are readonly/dynamic doesn't render a useless caret.
 */
export function filterGroups(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes
    .filter((n) => n && n.isGroup && !n.readonly && !n.dynamic)
    .map((n) => {
      const filtered = filterGroups(n.nodes || []);
      return {
        _id: n._id,
        name: n.name,
        path: n.path,
        isGroup: true,
        isBusy: Boolean(n.isBusy),
        hasChild: filtered.length > 0,
        teamId: n.teamId,
        nodes: filtered,
      };
    });
}

/** Walk a (filtered) group tree to find a node by its path. */
export function findGroupByPath(nodes, target) {
  const list = nodes || [];
  const direct = list.find((g) => g.path === target);
  if (direct) return direct;
  return list.reduce(
    (found, g) => found || findGroupByPath(g.nodes, target),
    null,
  );
}

/**
 * Walk the scope's full tree (groups + saved searches, incl. readonly/dynamic
 * groups) to find a group by `path`. Used for sibling naming — same segment
 * list as SavedSearchesTree `collectSiblingSegmentNames(node.nodes)`.
 */
export function findGroupByPathInScope(nodes, target) {
  const list = nodes || [];
  const direct = list.find((n) => n && n.isGroup && n.path === target);
  if (direct) return direct;
  return list.reduce(
    (found, n) => {
      if (found || !n || !n.isGroup) return found;
      return findGroupByPathInScope(n.nodes, target);
    },
    null,
  );
}

/**
 * Suggested default name for inline "+ New group" inputs:
 * "New group", "New group 2", … unique amongst sibling segment names
 * (groups + saved searches share one namespace; see pathConflict.js).
 */
export function suggestNewGroupName(siblings) {
  const base = l10n.SAVEDSEARCHESTREE.textNewGroupDefault;
  if (!siblings.includes(base)) return base;
  let i = 2;
  while (siblings.includes(`${base} ${i}`)) i += 1;
  return `${base} ${i}`;
}

const TreeRow = ({
  node, level, selectedPath, onSelect,
  expanded, onToggleExpanded, ensureOpen,
  addingForPath, onStartAdd, onAddSubmit, onAddCancel,
  validateName, suggestDefaultName,
  disabledPaths, allowAdd,   canAddSubgroupHere = true,
  testIdPrefix,
}) => {
  const isOpen = expanded.has(node.path);
  const hasChildren = (node.nodes && node.nodes.length > 0) || node.hasChild;
  const isSelected = selectedPath === node.path;
  const isAdding = addingForPath === node.path;
  const isDisabled = node.isBusy || disabledPaths.has(node.path);
  const showSubgroupAdd = allowAdd && canAddSubgroupHere;

  const handleRowClick = () => {
    if (!isDisabled) onSelect(node.path);
  };

  const handleClickArrow = (e) => {
    e.stopPropagation();
    if (hasChildren) onToggleExpanded(node.path);
  };

  const handleStartAdd = (e) => {
    e.stopPropagation();
    onStartAdd(node.path);
    ensureOpen(node.path);
  };

  return (
    <div
      className={cn('dropdownTreeItem', {
        act: isSelected,
        openedItem: isOpen,
        disabled: isDisabled,
      })}
      data-testid={`${testIdPrefix}Item`}
    >
      <div
        className="row"
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyPress={handleRowClick}
        style={{
          marginLeft: ITEM_PADDING_LEFT * level,
          width: `calc(100% - ${ITEM_PADDING_LEFT * level}px)`,
        }}
      >
        <If condition={hasChildren}>
          <span
            className="arrowItem"
            role="button"
            tabIndex={0}
            onClick={handleClickArrow}
            onKeyPress={handleClickArrow}
          >
            open/close
          </span>
        </If>
        <span className="iconSubject"><FolderIcon /></span>
        <span className="dropdownTreeItemName">{node.name}</span>
        <If condition={showSubgroupAdd && !isDisabled}>
          <IconButton
            className="onHover"
            onClick={handleStartAdd}
            title={l10n.SAVEDSEARCHESTREE.textCreateSubgroup}
            data-testid={`${testIdPrefix}AddSubgroup`}
          >
            <NewCollection />
          </IconButton>
        </If>
        <If condition={allowAdd && !canAddSubgroupHere && !isDisabled}>
          <Tooltip
            content={l10n.SAVEDSEARCHESTREE.textCreateSubgroupTooltip(MAX_GROUP_DEPTH)}
            placement="top"
          >
            <span>
              <IconButton
                className="onHover"
                disabled
                data-testid={`${testIdPrefix}AddSubgroupDisabled`}
              >
                <NewCollection />
              </IconButton>
            </span>
          </Tooltip>
        </If>
        <If condition={isSelected}>
          <SelectedCheck />
        </If>
      </div>

      <If condition={isAdding || (isOpen && node.nodes && node.nodes.length > 0)}>
        <div className="childList">
          <If condition={isAdding}>
            <div className="dropdownTreeItem">
              <div
                className="row"
                style={{
                  marginLeft: ITEM_PADDING_LEFT * (level + 1),
                  width: `calc(100% - ${ITEM_PADDING_LEFT * (level + 1)}px)`,
                }}
              >
                <span className="iconSubject"><FolderIcon /></span>
                <TreeItemEdit
                  name=""
                  defaultName={suggestDefaultName(node.path)}
                  onCancel={onAddCancel}
                  onSave={(value) => onAddSubmit(node.path, value)}
                  validation={(v) => validateName(node.path, v)}
                />
              </div>
            </div>
          </If>
          <If condition={isOpen && node.nodes && node.nodes.length > 0}>
            {node.nodes.map((child) => (
              <TreeRow
                key={child.path}
                node={child}
                level={level + 1}
                selectedPath={selectedPath}
                onSelect={onSelect}
                expanded={expanded}
                onToggleExpanded={onToggleExpanded}
                ensureOpen={ensureOpen}
                addingForPath={addingForPath}
                onStartAdd={onStartAdd}
                onAddSubmit={onAddSubmit}
                onAddCancel={onAddCancel}
                validateName={validateName}
                suggestDefaultName={suggestDefaultName}
                disabledPaths={disabledPaths}
                allowAdd={allowAdd}
                canAddSubgroupHere={
                  (child.path || '').split(NEW_PATH_DELIMITER).filter(Boolean).length < MAX_GROUP_DEPTH
                }
                testIdPrefix={testIdPrefix}
              />
            ))}
          </If>
        </div>
      </If>
    </div>
  );
};

const EMPTY_SET = new Set();

/**
 * Tree picker for saved-search groups, shared between the "Save this search"
 * and "Move to group" dialogs.
 *
 * Visual: DropdownTree-styled rows (matches "Add to collection") with a
 * single root-scope row at the top followed by recursive group rows.
 *
 * Pure presentational + minimal local state for the inline "+ New group"
 * input. Selection / expansion are controlled by the parent.
 *
 * `onCreateGroup` is optional — when omitted the picker is read-only (no
 * "+" affordances anywhere). When provided, a hover-revealed "+" appears on
 * every (non-disabled) group row plus a permanently visible "+" on the root
 * scope row.
 *
 * Pass `fullScopeNodes` (the scope's raw `nodes` before `filterGroups`) so
 * inline new-group naming matches the main tree: groups and saved searches
 * share one sibling namespace per `pathConflict.collectSiblingSegmentNames`.
 */
const SavedSearchGroupTree = ({
  groups, rootLabel,
  /** Full scope `nodes` (before filterGroups) — required for correct sibling naming vs saved searches. */
  fullScopeNodes,
  selectedPath, onSelectPath,
  expanded, setExpanded,
  disabledPaths, rootSelectable,
  onCreateGroup, onAddingChange,
  testIdPrefix, className,
}) => {
  const allowAdd = Boolean(onCreateGroup);
  const [addingForPath, setAddingForPath] = useState(null);
  const [creatingError, setCreatingError] = useState(null);

  // Surface the "input is open" state to the host so it can disable Ok until
  // the row is finished or cancelled (see SaveSearchDialog / showSaveSearchDialog).
  useEffect(() => {
    if (onAddingChange) onAddingChange(addingForPath !== null);
  }, [addingForPath, onAddingChange]);

  const toggleExpanded = useCallback((path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, [setExpanded]);

  const ensureOpen = useCallback((path) => {
    if (!path || path === ROOT_KEY) return;
    setExpanded((prev) => {
      if (prev.has(path)) return prev;
      const next = new Set(prev);
      next.add(path);
      return next;
    });
  }, [setExpanded]);

  const getSiblings = useCallback((parentPath) => {
    const useFull = fullScopeNodes != null;
    if (parentPath === ROOT_KEY) {
      return collectSiblingSegmentNames(
        useFull ? fullScopeNodes : groups,
      );
    }
    const parent = useFull
      ? findGroupByPathInScope(fullScopeNodes, parentPath)
      : findGroupByPath(groups, parentPath);
    return collectSiblingSegmentNames(parent?.nodes || []);
  }, [groups, fullScopeNodes]);

  const validateName = useCallback((parentPath, v) => {
    const value = (v || '').trim();
    if (!value || value.startsWith('.')) return false;
    return !getSiblings(parentPath).includes(value);
  }, [getSiblings]);

  const suggestDefaultName = useCallback(
    (parentPath) => suggestNewGroupName(getSiblings(parentPath)),
    [getSiblings],
  );

  const handleStartAdd = useCallback((parentPath) => {
    setAddingForPath(parentPath);
    setCreatingError(null);
  }, []);

  const handleAddCancel = useCallback(() => {
    setAddingForPath(null);
    setCreatingError(null);
  }, []);

  const handleAddSubmit = useCallback(async (parentPath, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setAddingForPath(null);
      return;
    }
    setCreatingError(null);
    try {
      const newPath = await onCreateGroup({
        parentPath: parentPath === ROOT_KEY ? null : parentPath,
        name: trimmed,
      });
      // Auto-select the freshly created group — saves the user a click and
      // matches what the legacy "+ New group" form did.
      if (typeof newPath === 'string') onSelectPath(newPath);
      setAddingForPath(null);
      ensureOpen(parentPath === ROOT_KEY ? '' : parentPath);
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      setCreatingError(
        status === 409
          ? l10n.SAVEDSEARCHESTREE.textDuplicateGroupInLocation
          : (err && err.message) || l10n.SEARCH.dialogSaveSearchErrorCreateGroup,
      );
      // Keep the input open so the user can retry.
    }
  }, [ensureOpen, onCreateGroup, onSelectPath]);

  const isRootDisabled = !rootSelectable || disabledPaths.has('');
  const isRootSelected = selectedPath === '';

  const handleRootClick = () => {
    if (!isRootDisabled) onSelectPath('');
  };

  return (
    <div className={cn(s.wrapper, className)} data-testid={testIdPrefix}>
      <div className="dropdownTree">
        <div className={cn('dropdownTreeList', s.list)}>
          <div
            className={cn('dropdownTreeItem openedItem', s.rootItem, {
              act: isRootSelected,
              disabled: isRootDisabled,
            })}
            data-testid={`${testIdPrefix}Root`}
          >
            <div
              className="row"
              role="button"
              tabIndex={0}
              onClick={handleRootClick}
              onKeyPress={handleRootClick}
              style={{ marginLeft: 0, width: '100%' }}
            >
              <span className="iconSubject"><FolderIcon /></span>
              <span className="dropdownTreeItemName">{rootLabel}</span>
              <If condition={allowAdd}>
                <IconButton
                  className={cn('onHover', s.rootAdd)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartAdd(ROOT_KEY);
                  }}
                  title={l10n.SAVEDSEARCHESTREE.textCreateRootGroup}
                  data-testid={`${testIdPrefix}AddRoot`}
                >
                  <NewCollection />
                </IconButton>
              </If>
              <If condition={isRootSelected}>
                <SelectedCheck />
              </If>
            </div>

            <div className="childList">
              <If condition={addingForPath === ROOT_KEY}>
                <div className="dropdownTreeItem">
                  <div
                    className="row"
                    style={{
                      marginLeft: ITEM_PADDING_LEFT,
                      width: `calc(100% - ${ITEM_PADDING_LEFT}px)`,
                    }}
                  >
                    <span className="iconSubject"><FolderIcon /></span>
                    <TreeItemEdit
                      name=""
                      defaultName={suggestDefaultName(ROOT_KEY)}
                      onCancel={handleAddCancel}
                      onSave={(value) => handleAddSubmit(ROOT_KEY, value)}
                      validation={(v) => validateName(ROOT_KEY, v)}
                    />
                  </div>
                </div>
              </If>

              {groups.map((g) => (
                <TreeRow
                  key={g.path}
                  node={g}
                  level={1}
                  selectedPath={selectedPath}
                  onSelect={onSelectPath}
                  expanded={expanded}
                  onToggleExpanded={toggleExpanded}
                  ensureOpen={ensureOpen}
                  addingForPath={addingForPath}
                  onStartAdd={handleStartAdd}
                  onAddSubmit={handleAddSubmit}
                  onAddCancel={handleAddCancel}
                  validateName={validateName}
                  suggestDefaultName={suggestDefaultName}
                  disabledPaths={disabledPaths}
                  allowAdd={allowAdd}
                  canAddSubgroupHere={
                    (g.path || '').split(NEW_PATH_DELIMITER).filter(Boolean).length < MAX_GROUP_DEPTH
                  }
                  testIdPrefix={testIdPrefix}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <If condition={creatingError}>
        <div className={s.error} data-testid={`${testIdPrefix}Error`}>
          {creatingError}
        </div>
      </If>
    </div>
  );
};

SavedSearchGroupTree.propTypes = {
  groups: arrayOf(shape({
    _id: string,
    name: string,
    path: string,
  })).isRequired,
  /** When set, sibling names include saved searches (matches main tree inline create). */
  fullScopeNodes: arrayOf(any),
  rootLabel: string.isRequired,
  selectedPath: string,
  onSelectPath: func.isRequired,
  expanded: instanceOf(Set).isRequired,
  setExpanded: func.isRequired,
  disabledPaths: instanceOf(Set),
  rootSelectable: bool,
  onCreateGroup: func,
  onAddingChange: func,
  testIdPrefix: string,
  className: string,
};

SavedSearchGroupTree.defaultProps = {
  fullScopeNodes: undefined,
  selectedPath: null,
  disabledPaths: EMPTY_SET,
  rootSelectable: true,
  onCreateGroup: null,
  onAddingChange: null,
  testIdPrefix: 'savedSearchGroupTree',
  className: '',
};

export default SavedSearchGroupTree;
